import { useIsMobile } from "@/hooks/use-mobile";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
  toYMD,
} from "@/lib/utils";
import { useUpdatePdcMutationFinance } from "@/services/school/commercial/installment/useUpdatePdcMutationFinance";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  PDC_AMOUNT,
  PDC_AMOUNT_RECEIVED,
  PDC_CLEARED_DATE,
  PDC_DATE,
  PDC_NO,
  PDC_REMARKS,
  PDC_STATUS,
  YEAR,
} from "./consts";
import { formValues, schema } from "./validations";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface UseUpdatePdcProps {
  onCancel: () => void;
  editData?: any;
}

const useUpdatePdc = ({ onCancel, editData }: UseUpdatePdcProps) => {
  const isMobile = useIsMobile();
  const { action, id, installmentIndex } = useParams();
  const queryClient = useQueryClient();

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [YEAR]: "",
      [PDC_NO]: "",
      [PDC_AMOUNT]: "",
      [PDC_DATE]: "",
      [PDC_REMARKS]: "",
      [PDC_AMOUNT_RECEIVED]: "",
      [PDC_CLEARED_DATE]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);
  const updatePdcMutation = useUpdatePdcMutationFinance({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdc", installmentIndex] });
      form.reset();
      onCancel();
    },
  });

  useEffect(() => {
    if (editData) {
      form.reset({
        [YEAR]: String(editData.year) || "",
        [PDC_NO]: String(editData.pdcNumber) || "",
        [PDC_AMOUNT]: String(editData.pdcAmount) || "",
        [PDC_AMOUNT_RECEIVED]: editData?.pdcAmountReceived
          ? String(editData?.pdcAmountReceived)
          : "",
          [PDC_STATUS]: String(editData?.pdcStatus),
        [PDC_DATE]: toYMD(editData.pdcDate) || "",
        [PDC_CLEARED_DATE]: toYMD(editData.pdcClearedDate) || "",
        [PDC_REMARKS]: editData.pdcRemarks || "",
      });
    } else {
      form.reset({
        [YEAR]: "",
        [PDC_NO]: "",
        [PDC_AMOUNT]: "",
        [PDC_DATE]: "",
        [PDC_REMARKS]: "",
        [PDC_AMOUNT_RECEIVED]: "",
        [PDC_CLEARED_DATE]: "",
      });
    }
  }, [editData]);

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(data);
    _data.installmentId = installmentIndex as string;
    if (_data.pdcClearedDate)
      _data.pdcClearedDate = toYMD(_data.pdcClearedDate);
    _data.pdcDate = toYMD(_data.pdcDate);
    if (editData && editData.pdcId) {
      _data.pdcId = editData.pdcId;
    }
    updatePdcMutation.mutate(_data as any);
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isMobile,
  };
};

export default useUpdatePdc;
